import React, { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Minus, Plus, Trash2, Phone, Mail, MessageCircle } from "lucide-react";
import { useEnquiryCart } from "@/contexts/EnquiryCartContext";
import { useSettings } from "@/contexts/SettingsContext";
import { buildEnquiryMessage, buildWhatsAppLink } from "@/lib/whatsapp";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function EnquiryCart() {
    const { items, open, setOpen, updateQty, updateNotes, removeItem, clear } = useEnquiryCart();
    const { settings } = useSettings();
    const [form, setForm] = useState({ shop_name: "", contact_name: "", phone: "", email: "", city: "", message: "" });
    const [submitting, setSubmitting] = useState(false);
    const onChange = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

    const persistEnquiry = async () => {
        const payload = { ...form, items: items.map((i) => ({ product_id: i.product_id, name: i.name, sku: i.sku, quantity: i.quantity, notes: i.notes || null })) };
        try { await api.post("/enquiries", payload); } catch (e) { console.warn("Enquiry not persisted", e?.message); }
    };

    const onWhatsApp = async () => {
        if (items.length === 0) { toast.error("Your enquiry list is empty"); return; }
        setSubmitting(true);
        await persistEnquiry();
        const msg = buildEnquiryMessage({ items, shopName: form.shop_name, contactName: form.contact_name, city: form.city, note: form.message });
        window.open(buildWhatsAppLink(settings.whatsapp_number, msg), "_blank");
        setSubmitting(false);
        toast.success("Opening WhatsApp with your enquiry");
    };

    const onEmail = async () => {
        if (items.length === 0) { toast.error("Your enquiry list is empty"); return; }
        await persistEnquiry();
        const subject = encodeURIComponent("Enquiry from Gold by SRN catalog");
        const body = encodeURIComponent(buildEnquiryMessage({ items, shopName: form.shop_name, contactName: form.contact_name, city: form.city, note: form.message }));
        window.location.href = `mailto:${settings.email}?subject=${subject}&body=${body}`;
    };

    return (
        <Sheet open={open} onOpenChange={setOpen}>
            <SheetContent
                side="right"
                className="bg-paper border-l border-soft text-ink w-full sm:max-w-md overflow-y-auto"
            >
                <SheetHeader className="border-b border-soft pb-4 -mx-6 px-6">
                    <SheetTitle className="font-serif text-3xl text-ink text-left">Your Enquiry</SheetTitle>
                    <p className="overline text-left">{items.length} {items.length === 1 ? 'piece' : 'pieces'}</p>
                </SheetHeader>

                <div className="mt-6 space-y-4">
                    {items.length === 0 && (
                        <div data-testid="cart-empty" className="text-center py-14">
                            <p className="font-serif text-2xl text-ink">Nothing yet.</p>
                            <p className="text-sm text-muted-ink mt-3">Add pieces from the catalog to enquire on WhatsApp.</p>
                        </div>
                    )}

                    {items.map((it) => (
                        <div key={it.product_id} className="flex gap-3 pb-4 border-b border-soft" data-testid={`cart-item-${it.sku}`}>
                            <img src={it.image} alt={it.name} className="w-20 h-24 object-cover bg-ivory" />
                            <div className="flex-1">
                                <div className="flex items-start justify-between gap-2">
                                    <div>
                                        <p className="overline">{it.purity}{it.weight_grams ? ` · ${it.weight_grams}g` : ''}</p>
                                        <p className="font-serif text-lg text-ink leading-tight mt-1">{it.name}</p>
                                        <p className="text-[10px] tracking-[0.18em] text-muted-ink mt-1">SKU · {it.sku}</p>
                                    </div>
                                    <button onClick={() => removeItem(it.product_id)} className="text-muted-ink hover:text-bronze-deep" data-testid={`cart-remove-${it.sku}`}>
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                                <div className="flex items-center gap-3 mt-2">
                                    <div className="flex items-center border border-soft">
                                        <button onClick={() => updateQty(it.product_id, it.quantity - 1)} className="px-2 py-1 text-bronze-deep" data-testid={`cart-dec-${it.sku}`}><Minus size={11} /></button>
                                        <span className="px-3 text-sm" data-testid={`cart-qty-${it.sku}`}>{it.quantity}</span>
                                        <button onClick={() => updateQty(it.product_id, it.quantity + 1)} className="px-2 py-1 text-bronze-deep" data-testid={`cart-inc-${it.sku}`}><Plus size={11} /></button>
                                    </div>
                                </div>
                                <Input
                                    value={it.notes || ""}
                                    onChange={(e) => updateNotes(it.product_id, e.target.value)}
                                    placeholder="Note (size, diameter, finish…)"
                                    data-testid={`cart-note-${it.sku}`}
                                    className="mt-2 bg-white border-soft text-sm h-9 placeholder:text-muted-ink/70"
                                />
                            </div>
                        </div>
                    ))}
                </div>

                {items.length > 0 && (
                    <>
                        <div className="divider-dots my-8" />
                        <div className="space-y-3">
                            <p className="overline">Your Details (optional)</p>
                            <div className="grid grid-cols-2 gap-3">
                                <Field><Label className="text-[10px] tracking-[0.18em] text-muted-ink uppercase">Shop name</Label><Input data-testid="cart-input-shop" value={form.shop_name} onChange={onChange('shop_name')} className="bg-white border-soft h-9" /></Field>
                                <Field><Label className="text-[10px] tracking-[0.18em] text-muted-ink uppercase">Your name</Label><Input data-testid="cart-input-name" value={form.contact_name} onChange={onChange('contact_name')} className="bg-white border-soft h-9" /></Field>
                                <Field><Label className="text-[10px] tracking-[0.18em] text-muted-ink uppercase">Phone</Label><Input data-testid="cart-input-phone" value={form.phone} onChange={onChange('phone')} className="bg-white border-soft h-9" /></Field>
                                <Field><Label className="text-[10px] tracking-[0.18em] text-muted-ink uppercase">City</Label><Input data-testid="cart-input-city" value={form.city} onChange={onChange('city')} className="bg-white border-soft h-9" /></Field>
                            </div>
                            <Textarea data-testid="cart-input-message" value={form.message} onChange={onChange('message')} placeholder="Anything specific you'd like to mention?" className="bg-white border-soft" />
                        </div>

                        <div className="mt-6 space-y-3">
                            <button
                                onClick={onWhatsApp}
                                disabled={submitting}
                                data-testid="cart-btn-whatsapp"
                                className="w-full h-12 bg-[#25D366] hover:bg-[#1DA851] text-white uppercase tracking-[0.22em] text-[11px] flex items-center justify-center gap-2"
                            >
                                <MessageCircle size={14} /> Enquire on WhatsApp
                            </button>
                            <div className="grid grid-cols-2 gap-3">
                                <a
                                    href={`tel:${settings.phone_display.replace(/\s/g, '')}`}
                                    data-testid="cart-btn-call"
                                    className="h-11 flex items-center justify-center gap-2 border border-ink/80 text-ink hover:bg-ink hover:text-paper transition-colors uppercase text-[11px] tracking-[0.22em]"
                                >
                                    <Phone size={13} /> Call
                                </a>
                                <button
                                    onClick={onEmail}
                                    data-testid="cart-btn-email"
                                    className="h-11 flex items-center justify-center gap-2 border border-ink/80 text-ink hover:bg-ink hover:text-paper transition-colors uppercase text-[11px] tracking-[0.22em]"
                                >
                                    <Mail size={13} /> Email
                                </button>
                            </div>
                            <button onClick={clear} data-testid="cart-btn-clear" className="w-full text-[10px] text-muted-ink uppercase tracking-[0.24em] hover:text-bronze-deep pt-3">
                                Clear list
                            </button>
                        </div>
                    </>
                )}
            </SheetContent>
        </Sheet>
    );
}

const Field = ({ children }) => <div className="space-y-1.5">{children}</div>;
